<template>
    <div>
        <div>
            <!-- Header -->
            <div class="flex justify-between mb-4 align-items-center">
                <h4 class="font-medium text-lg">
                    Saved Replies
                </h4>

                <button @click.prevent="showNewSavedReplyModal = true"
                        class="btn btn-primary">
                    New Saved reply
                </button>
            </div>

            <!-- Filters -->
            <div class="flex flex-wrap items-center mb-4 gap-x-2 gap-y-2 sm:gap-y-0">
                <text-input
                    input-label=""
                    placeholder="Search Saved Replies"
                    v-model="filterText"
                    @input="fetchSavedReplies()"
                ></text-input>

                <simple-dropdown v-model="categoryFilter" :label="categoryFilter | titlecase" @input="fetchSavedReplies()"
                                 button-class="h-10 sm:h-9 pl-3 pr-2 justify-center items-center rounded-md border border-gray-300 shadow-sm py-2 bg-white font-normal text-gray-700 hover:bg-indigo-50 hover:border-indigo-300 hover:text-indigo-600 focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-indigo-600">
                    <template slot="buttonOptions">
                        <simple-dropdown-option value="all"
                                                key="allCategoryGilter + Math.random() * 99999">
                            All
                        </simple-dropdown-option>
                        <simple-dropdown-option v-for="category in categories"
                                                :value="category"
                                                :key="category + 'filter'">
                            {{ category | titlecase }}
                        </simple-dropdown-option>
                    </template>
                </simple-dropdown>

                <toggle @input="fetchSavedReplies()" v-model="showArchived">
                    Show Archived
                </toggle>
            </div>

            <!-- Saved Replies -->
            <div v-if="!loading">
                <div v-if="savedRepliesList && savedRepliesList.length > 0">
                    <div v-for="savedReply in savedRepliesList" class="cursor-pointer py-2 my-2 rounded-md bg-white shadow-sm"
                         :key="savedReply.id">
                        <saved-reply transition="fade" transition-mode="out-in"
                                     :roles="roles"
                                     :saved-reply="savedReply"
                                     :categories="categories"
                                     @saved-reply-archived-or-unarchived="removeSavedReplyFromArray">
                        </saved-reply>
                    </div>

                    <simple-pagination
                        v-if="savedRepliesList"
                        :meta="paginationData.meta"
                        padding="2"
                        class="mt-2"
                        per-page="10"
                        side-navigation-count="5"
                        @page-changed="fetchSavedReplies">
                    </simple-pagination>
                </div>

                <div v-else class="flex justify-center py-24">
                    <span class="text-gray-600">No saved replies with those filters</span>
                </div>
            </div>

            <div v-else class="flex justify-center py-24">
                <icon-refresh v-if="loading" class="h-10 w-10 text-indigo-600 animate-spin"/>
            </div>
        </div>

        <simple-modal :show="showNewSavedReplyModal" title="Create Saved Reply"
                      max-width="max-w-2xl" @close="closeCreateSavedReplyModal();">
            <div class="grid grid-cols-4 gap-4">
                <div class="col-span-4 sm:col-span-2">
                    <label class="font-normal">Category</label>
                    <el-select class="w-full"
                               id="category"
                               v-model="form.category"
                               placeholder="Select Category">
                        <el-option v-for="category in categories" :value="category" :key="category" :label="category | titlecase"></el-option>
                    </el-select>

                    <span class="invalid-feedback" :class="{'d-block': form.errors.has('category')}"
                          v-show="form.errors.has('category')">
                        {{ form.errors.get("category") }}
                    </span>
                </div>

                <div class="col-span-4 sm:col-span-2">
                    <label class="font-normal">Type</label>
                    <el-select id="type" v-model="form.type" class="w-full"
                               placeholder="Select type">
                        <el-option value="email" label="Email"></el-option>
                        <el-option value="sms" label="SMS"></el-option>
                    </el-select>

                    <span v-show="form.errors.has('type')">
                        {{ form.errors.get("type") }}
                    </span>
                </div>

                <div class="col-span-4">
                    <label class="font-medium">Audience</label>

                    <el-select id="audience" v-model="form.audience" class="w-full"
                               :class="{'is-invalid': form.errors.has('audience')}"
                               multiple clearable
                               placeholder="Select Audience">
                        <el-option v-for="role in roles" :key="role" :value="role" :label="role | titlecase"></el-option>
                    </el-select>

                    <span class="invalid-feedback" v-show="form.errors.has('audience')">
                        {{ form.errors.get("audience") }}
                    </span>
                </div>


                <div class="col-span-4 sm:col-span-2">
                    <text-input
                        class="w-full" input-label="Reply Name"
                        placeholder="Sharing Online Inspection Report (Landlord)"
                        v-model="form.name" :has-errors="form.errors.has('name')"
                    ></text-input>
                </div>

                <div class="col-span-4 sm:col-span-2" v-show="!formTypeSMS">
                    <text-input
                        class="w-full" input-label="Subject"
                        placeholder="(Optional) Online Inspection Report now available"
                        v-model="form.subject" :has-errors="form.errors.has('subject')"
                    ></text-input>
                </div>

                <div class="col-span-4">
                    <!--body heading / count / tag select-->
                    <div class="flex flex-wrap items-end justify-between pb-3">
                        <div class="flex flex-row items-center">
                            <label class="font-medium">Body</label>
                            <p v-if="formTypeSMS" class="flex items-end text-sm px-2 mb-1"
                               :class="smsCharsRemaining < 0 ? 'text-red-600' : 'text-gray-400'">
                                {{ smsCharsRemaining }}
                            </p>
                        </div>

                        <el-select v-model="selectedTag"
                                   :filterable=true
                                   placeholder="Select Tag To Insert"
                                   @change="insertAtCaret"
                                   :disabled="smsCharsRemaining <= 0 && !formTypeSMS">

                            <el-option-group
                                v-for="group in tagGroups"
                                :key="group.label"
                                :label="group.label"
                                class="custom-drop-group-header">
                                <el-option
                                    v-for="item in group.options"
                                    :key="item.value"
                                    class="custom-group-item"
                                    :label="item.label"
                                    :value="item.value">
                                </el-option>
                            </el-option-group>
                        </el-select>
                    </div>

                    <textarea class="form-control" ref="savedReplyBodyField" v-model="form.body"
                              :maxlength="formTypeSMS ? smsMaxLength : 524288"
                              type="text" style="height:200px" id="body" name="body"
                              :class="{'is-invalid': form.errors.has('body')}">
                    </textarea>
                    <span class="invalid-feedback" v-show="form.errors.has('body')">
                        {{ form.errors.get("body") }}
                    </span>
                </div>

                <div class="col-span-4 flex justify-end space-x-2">
                    <button class="btn btn-sm btn-default waves-effect waves-light pull-left"
                            @click.prevent="closeCreateSavedReplyModal()">Cancel
                    </button>
                    <button class="btn btn-sm btn-success waves-effect waves-light pull-right"
                            @click.prevent="createSavedReply()">Create
                    </button>
                </div>
            </div>
        </simple-modal>
    </div>
</template>

<script>
import TagGroupsMixin from "../../mixins/tag-groups-mixin";

export default {
    name: "SavedRepliesList",

    mixins: [TagGroupsMixin],

    data () {
        return {
            savedRepliesList: [],
            paginationData: {},
            loading: true,
            form: new AppForm({
                body: "",
                audience: [],
                category: "maintenance",
                type: "email"
            }),
            showNewSavedReplyModal: false,
            showCharCount: false,
            smsMaxLength: 350,

            filterText: "",
            showArchived: false,
            categoryFilter: "all",

            roles: [],
            categories: [],
        };
    },

    watch: {
        "form.type" (newValue) {
            if (newValue === "sms" && this.form.body.length > this.smsMaxLength) {
                this.form.body = this.form.body.slice(0, this.smsMaxLength);
                this.showCharCount = true;
            } else {
                this.showCharCount = false;
            }
        },

        //tags insert into the form.body object and skip the max length on the input
        "form.body" (newValue) {
            if (this.formTypeSMS && newValue.length > 350) {
                this.form.body = this.form.body.slice(0, this.smsMaxLength);
            }
        }
    },

    computed: {
        inboxUrl () {
            return "/inbox";
        },

        tagGroups () {
            if (this.form.category === "maintenance") {
                return this.tagGroupAlways.concat(this.tagGroupProperty).concat(this.tagGroupMaintenance);
            }

            if (this.form.category === "maintenance requests") {
                return this.tagGroupAlways.concat(this.tagGroupMaintenanceRequests);
            }

            return this.form.category === "inspections" ?
                this.tagGroupAlways.concat(this.tagGroupProperty).concat(this.tagGroupInspections) :
                this.tagGroupAlways;
        },

        smsCharsRemaining () {
            return this.smsMaxLength - this.form.body.length;
        },

        formTypeSMS () {
            return this.form && this.form.type === "sms";
        }
    },

    mounted () {
        this.fetchSavedReplies();
        this.fetchSettings();
    },

    methods: {
        fetchSettings () {
            axios.get("/savedreplies/settings")
            .then((response) => {
                this.roles = response.data.roles;
                this.categories = response.data.categories;
            })
            .catch((error) => {
                toastr.error(error.message);
            });
        },

        removeSavedReplyFromArray (id) {
            let index = this.savedRepliesList.findIndex(s => s.id === id);
            if (index !== -1) {
                this.savedRepliesList.splice(index, 1);

                if (_.has(this, "paginationData.meta.to")) {
                    this.$set(this.paginationData.meta, "to", this.paginationData.meta.to - 1);
                }
                if (_.has(this, "paginationData.meta.total")) {
                    this.$set(this.paginationData.meta, "total", this.paginationData.meta.total - 1);
                }
                if (this.savedRepliesList.length === 0) {
                    this.fetchSavedReplies({page: 1});
                }
            }
        },

        closeCreateSavedReplyModal () {
            this.showNewSavedReplyModal = false;
            this.form = new AppForm({
                body: "",
                category: "maintenance",
                type: "email"
            });
        },

        fetchSavedReplies: _.debounce(function (paginationSettings = null) {
            this.loading = true;
            axios.get("/savedreplies", {
                params: {
                    filter_text: this.filterText,
                    archived: this.showArchived,
                    category: this.categoryFilter,
                    page: (paginationSettings && paginationSettings.pageNumber) ? paginationSettings.pageNumber : 1,
                    per_page: (paginationSettings && paginationSettings.perPage) ? paginationSettings.perPage : 10,
                }
            })
            .then(response => {
                this.savedRepliesList = response.data.data;
                this.paginationData = response.data;
                this.loading = false;
            })
            .catch(() => {
                this.loading = false;
                toastr.error("Error retrieving saved replies.");
            });
        }, 300),

        createSavedReply () {
            if (this.formTypeSMS) {
                this.form.subject = "";
            }

            App.post("savedreplies/create", this.form)
            .then((response) => {
                toastr.success("New Saved Reply Created");
                this.savedRepliesList.unshift(response);
                this.closeCreateSavedReplyModal();
            })
            .catch(error => {
                toastr.error("Could Not Create Saved Reply. Error: " + error.message);
            });
        },
    },
};
</script>
